import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Download, MapPin, DollarSign, ArrowLeft, ChevronRight, LogIn, LogOut } from "lucide-react";
import { Link } from "react-router-dom";

const fmt$ = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

// Cash Drawer History — admin view of every open/close count from the
// kiosk, with location/type filters and CSV export. Sits alongside the
// other financial reports under Insights.
export default function CashDrawerHistoryPage() {
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationId, setLocationId] = useState("all");
  const [drawerType, setDrawerType] = useState("all");
  const [days, setDays] = useState("30");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/locations").then(r => setLocations(r.data || [])).catch(() => setLocations([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { days: Number(days) || 30 };
    if (locationId !== "all") params.location_id = locationId;
    if (drawerType !== "all") params.drawer_type = drawerType;
    api.get("/cash-drawer/history", { params })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [locationId, drawerType, days]);

  const summary = useMemo(() => {
    const closes = rows.filter(r => r.drawer_type === "close");
    const totalVar = closes.reduce((sum, r) => sum + (Number(r.variance) || 0), 0);
    const shorts = closes.filter(r => (r.variance || 0) < 0).length;
    const overs = closes.filter(r => (r.variance || 0) > 0).length;
    return {
      total: rows.length,
      opens: rows.filter(r => r.drawer_type === "open").length,
      closes: closes.length,
      totalVar,
      shorts,
      overs,
      exact: closes.length - shorts - overs,
    };
  }, [rows]);

  const exportCsv = () => {
    const head = [
      "Date", "Type", "Location", "Staff",
      "Cash in Drawer", "Cash Dropped", "Opening", "Cash Sales",
      "Expected", "Variance", "Note",
    ];
    const lines = [head.join(",")];
    for (const r of rows) {
      lines.push([
        fmtDateTime(r.created_at),
        r.drawer_type,
        `"${(r.location_name || "").replace(/"/g, '""')}"`,
        `"${(r.user_name || "").replace(/"/g, '""')}"`,
        (r.cash_in_drawer ?? "").toString(),
        (r.cash_dropped ?? "").toString(),
        (r.opening_cash ?? "").toString(),
        (r.cash_sales ?? "").toString(),
        (r.expected_cash ?? "").toString(),
        (r.variance ?? "").toString(),
        `"${(r.note || "").replace(/"/g, '""')}"`,
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cash-drawer-history-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="cash-drawer-history-page">
      <Link to="/reports" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-2 font-['Manrope']">
        <ArrowLeft className="w-3 h-3" /> Back to Reports
      </Link>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Cash Drawer</h1>
          <p className="text-slate-500 text-sm mt-1 font-['Manrope']">
            {summary.opens} opens · {summary.closes} closes · last {days} days
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px] h-10 border-slate-200 bg-white rounded-full text-sm" data-testid="cdh-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="w-[180px] h-10 border-slate-200 bg-white rounded-full text-sm" data-testid="cdh-location">
              <MapPin className="w-3.5 h-3.5 mr-1 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={drawerType} onValueChange={setDrawerType}>
            <SelectTrigger className="w-[140px] h-10 border-slate-200 bg-white rounded-full text-sm" data-testid="cdh-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All counts</SelectItem>
              <SelectItem value="open">Opens only</SelectItem>
              <SelectItem value="close">Closes only</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="cdh-export"
            className="border-slate-200 rounded-full h-10 px-4 text-sm">
            <Download className="w-3.5 h-3.5 mr-1.5" />Export CSV
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Closes" value={summary.closes} sub={`${summary.opens} opens`} icon={LogOut} color="rose" />
        <SummaryCard label="Exact" value={summary.exact} sub="no variance" icon={DollarSign} color="emerald" />
        <SummaryCard label="Over" value={summary.overs} sub="more than expected" icon={DollarSign} color="amber" />
        <SummaryCard label="Net variance" value={fmt$(summary.totalVar)} sub={`${summary.shorts} short`} icon={DollarSign} color={summary.totalVar >= 0 ? "emerald" : "rose"} />
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden" data-testid="cdh-table-wrap">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-['Outfit'] text-base font-semibold text-slate-900">Recent counts</h3>
          <span className="text-xs text-slate-400 font-['Manrope']">{rows.length} records</span>
        </div>
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">
            No cash drawer counts found. Submit one from <Link to="/cash-drawer" className="underline text-slate-700">/cash-drawer</Link>.
          </div>
        ) : (
          <table className="w-full" data-testid="cdh-table">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100">
                <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">When</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Type</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Location</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Staff</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">In drawer</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Dropped</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Expected</th>
                <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Variance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.drawer_id} onClick={() => setActive(r)}
                  data-testid={`cdh-row-${r.drawer_id}`}
                  className="border-b border-slate-100 hover:bg-slate-50/40 transition-colors cursor-pointer">
                  <td className="px-6 py-3.5 text-sm text-slate-900">{fmtDateTime(r.created_at)}</td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      r.drawer_type === "open"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-rose-50 text-rose-700"
                    }`}>
                      {r.drawer_type === "open" ? <LogIn className="w-3 h-3" /> : <LogOut className="w-3 h-3" />}
                      {r.drawer_type === "open" ? "Open" : "Close"}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-slate-600">{r.location_name || "—"}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-900">{r.user_name || "—"}</td>
                  <td className="px-4 py-3.5 text-sm text-right text-slate-900 tabular-nums">{fmt$(r.cash_in_drawer)}</td>
                  <td className="px-4 py-3.5 text-sm text-right text-slate-500 tabular-nums">
                    {r.drawer_type === "close" ? fmt$(r.cash_dropped) : "—"}
                  </td>
                  <td className="px-4 py-3.5 text-sm text-right text-slate-500 tabular-nums">
                    {r.drawer_type === "close" ? fmt$(r.expected_cash) : "—"}
                  </td>
                  <td className="px-6 py-3.5 text-sm text-right font-semibold tabular-nums">
                    {r.drawer_type === "close" ? (
                      <span className={
                        (r.variance || 0) === 0 ? "text-emerald-600"
                        : (r.variance || 0) < 0 ? "text-rose-600"
                        : "text-amber-600"
                      }>
                        {(r.variance || 0) >= 0 ? "+" : "−"}{fmt$(Math.abs(r.variance || 0))}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {active && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setActive(null)} data-testid="cdh-detail-overlay">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6 animate-slideIn" onClick={e => e.stopPropagation()}
            data-testid="cdh-detail-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Outfit'] text-xl font-semibold">
                {active.drawer_type === "open" ? "Opening count" : "Closing count"}
              </h3>
              <button onClick={() => setActive(null)} data-testid="cdh-detail-close"
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 font-['Manrope']">{fmtDateTime(active.created_at)}</p>
            <p className="text-sm text-slate-900 font-medium mt-1">{active.user_name} · {active.location_name}</p>

            <div className="mt-5 space-y-2 bg-slate-50 rounded-2xl p-4 text-sm">
              <Row label="Cash in drawer" value={fmt$(active.cash_in_drawer)} />
              {active.drawer_type === "close" && (
                <>
                  <Row label="Opening cash" value={fmt$(active.opening_cash)} />
                  <Row label="Cash sales today" value={`+ ${fmt$(active.cash_sales)}`} className="text-emerald-700" />
                  <Row label="Cash dropped" value={`− ${fmt$(active.cash_dropped)}`} className="text-rose-700" />
                  <div className="border-t border-slate-200 my-2" />
                  <Row label="Expected" value={fmt$(active.expected_cash)} bold />
                  <Row
                    label="Variance"
                    value={`${(active.variance || 0) >= 0 ? "+" : "−"}${fmt$(Math.abs(active.variance || 0))}`}
                    bold
                    className={
                      (active.variance || 0) === 0 ? "text-emerald-700"
                      : (active.variance || 0) < 0 ? "text-rose-700"
                      : "text-amber-700"
                    }
                  />
                </>
              )}
            </div>
            {active.note && (
              <div className="mt-4 bg-amber-50 border border-amber-100 rounded-2xl p-3 text-sm text-amber-900">
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1">Note</p>
                {active.note}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, color = "slate" }) {
  const palette = {
    rose: "text-rose-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    slate: "text-slate-900",
  }[color] || "text-slate-900";
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5">
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">{label}</span>
      <p className={`font-['Outfit'] text-3xl font-semibold tabular-nums mt-1 ${palette}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1 font-['Manrope']">{sub}</p>}
    </div>
  );
}

function Row({ label, value, bold, className = "" }) {
  return (
    <div className={`flex items-center justify-between ${className}`}>
      <span className="text-slate-500">{label}</span>
      <span className={`tabular-nums ${bold ? "font-semibold text-base" : ""}`}>{value}</span>
    </div>
  );
}
